import { apiService } from '../../../shared/services/apiService';
import { downloadBinaryFile } from '../../../shared/utils/apiUtils';
import { getFilteredTruthyFieldsObj } from '../../../shared/utils/sortingAndGroupingUtil';
import {
  GetScanDataByInbodyIdResponse,
  ManualScanData,
} from '../../interfaces/customerInbodyData';
import { InbodyType } from '../../../shared/constants/inbody-info/InbodyType';
import { MealPlanOnScanDayInfo } from '../../interfaces/mealPlanOnScanDayInfo';
import { ScanMetadata } from '../../interfaces/scanMetadata';
import { KeyBodyMetricsInfo } from '../../interfaces/keyBodyMetricsInfo';
import { apiStatusStore } from '../../../shared/stores/apiStatusStore';
import {
  ScanService,
  GetInbodyIndicatorsResponse,
  CustomerInbodyTypeOption,
  UpdateScansFromInbodyResponse,
  DownloadInbodyResultSheetParams,
  RequestConfig,
} from './types';

export const nutritionScanService: ScanService = {
  async getScanDataByInbodyId(inbodyId: number | string, ignoreErrorCodes?: number[]) {
    const resp = await apiService.get<GetScanDataByInbodyIdResponse>(
      `api/coach/inbody/${inbodyId}`,
      { ignoreErrorCodes },
    );

    return resp.data;
  },

  async createScanManually(customerId: number, scanDate: ManualScanData) {
    const filteredScanDate = getFilteredTruthyFieldsObj(scanDate);

    const resp = await apiService.post(
      `api/coach/inbody/manual/${customerId}`,
      filteredScanDate,
    );

    return resp.data;
  },

  // connect scan from inbody to customer by inbody id, get new scan from inbody
  async updateScansFromInbody(customerId: number) {
    const resp = await apiService.put<UpdateScansFromInbodyResponse>(
      `/api/coach/inbody/update/${customerId}`,
    );

    return resp.data;
  },

  async getInbodyIndicators(customerId: number, userInbodyType: InbodyType | 'ALL') {
    const resp = await apiService.post<GetInbodyIndicatorsResponse>(
      `/api/users/${customerId}/inbody-data`,
      { userInbodyType },
      // allFields: return every indicator (false would drop indicators not supported by the inbody type);
      { params: { allFields: true } },
    );

    return resp.data;
  },

  async getCustomerInbodyTypeOptions(customerId: number) {
    const resp = await apiService.get<CustomerInbodyTypeOption[]>(
      `api/users/${customerId}/inbody-types`,
    );

    return resp.data;
  },

  async getScanDates(customerId: number, entityId?: number, config?: RequestConfig) {
    const resp = await apiService.get<ScanMetadata[]>(
      `api/users/${customerId}/scan-dates`,
      { ...config },
    );

    return resp.data;
  },

  async getMealPlansOnScanDay(customerId: number) {
    const resp = await apiService.get<MealPlanOnScanDayInfo[]>(
      `api/coach/inbodydata/${customerId}/mealplan`,
    );

    return resp.data;
  },

  async getKeyBodyMetrics(customerId: number) {
    const resp = await apiService.get<KeyBodyMetricsInfo>(
      `api/coach/user/${customerId}/stats/body-metrics`,
    );

    return resp.data;
  },

  async downloadInbodyResultSheet({
    customerId,
    scanId,
    fileName,
  }: DownloadInbodyResultSheetParams) {
    const resp = await apiService.get<ArrayBuffer>(
      `/api/users/${customerId}/inbody-data/${scanId}/result-sheet`,
      { responseType: 'arraybuffer' },
    );

    downloadBinaryFile(resp.data, 'image/png', `${fileName}.png`);
  },

  // Unavailable APIs - throw Error and show message
  async getProgressReportUrl(customerId: number) {
    const errorMessage = `Progress report is not available for NUTRITION customers ${customerId}`;
    apiStatusStore.addErrorMessage(errorMessage);
    throw new Error(errorMessage);
  },
};
